/**
 * Session Replay Loader
 *
 * Dynamically injects the Session Replay SDK script and bridges it with
 * the Web SDK's session ID and user ID. The SR SDK owns recording,
 * batching and sending — we only orchestrate its lifecycle.
 *
 * Expected SR SDK public API (window.b2metric):
 *   startSessionReplay()
 *   stopSessionReplay()
 *   isSessionReplayActive() → boolean
 *   identify(userId)
 *   setSessionId(sessionId)   ← SR team needs to expose this
 */
export default class SessionReplayLoader {
  constructor(tracker) {
    this.tracker = tracker;
    this._loaded = false;
    this._loading = false;
    this._scriptEl = null;
  }

  /**
   * Inject the SR script and initialise it with current session/user IDs.
   * Safe to call multiple times — loads the script only once.
   */
  async load() {
    if (this._loaded) {
      this._sync();
      return true;
    }

    if (this._loading) {
      return this._waitForLoad();
    }

    const { sessionReplayScriptUrl, sessionReplayHost, sessionReplaySiteId } = this.tracker.config;

    if (!sessionReplayScriptUrl) {
      this.tracker.logger.error('[B2M SessionReplay] sessionReplayScriptUrl is not configured');
      return false;
    }
    if (!sessionReplayHost) {
      this.tracker.logger.error('[B2M SessionReplay] sessionReplayHost is not configured');
      return false;
    }
    if (!sessionReplaySiteId) {
      this.tracker.logger.error('[B2M SessionReplay] sessionReplaySiteId is not configured');
      return false;
    }

    this._loading = true;
    this.tracker.logger.log('[B2M SessionReplay] Loading SR script:', sessionReplayScriptUrl);

    // In custom-session-id mode SR script does NOT auto-start and waits for
    // the host page to call startSessionReplay(sessionId). It signals readiness
    // via the `b2metric:ready` event — register the listener BEFORE injecting
    // the script so a synchronous dispatch can't race past us.
    const useCustomSessionId = !!this.tracker.config.sessionReplayUseCustomSessionId;
    const readyPromise = useCustomSessionId ? this._waitForReadyEvent() : null;

    try {
      const attrs = {
        'data-host': sessionReplayHost,
        'data-site-id': String(sessionReplaySiteId)
      };
      if (useCustomSessionId) {
        attrs['data-use-custom-session-id'] = 'true';
      }
      // SR SDK reads data-host and data-site-id from document.currentScript,
      // so we must set these attributes on the injected script element.
      await this._injectScript(sessionReplayScriptUrl, attrs);
      if (readyPromise) await readyPromise;
      this._loaded = true;
      this._loading = false;
      this._sync();
      this.tracker.logger.log('[B2M SessionReplay] SR script loaded and initialised');
      return true;
    } catch (e) {
      this._loading = false;
      this.tracker.logger.error('[B2M SessionReplay] Failed to load SR script:', e);
      return false;
    }
  }

  _waitForReadyEvent() {
    return new Promise(resolve => {
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      window.addEventListener('b2metric:ready', finish, { once: true });
      // Fallback so a missing event can't permanently block load()
      setTimeout(finish, 5000);
    });
  }

  /**
   * Push current sessionId + userId into the already-loaded SR SDK.
   * Called after load and whenever either value changes.
   */
  _sync() {
    if (!this._loaded || !window.b2metric) return;

    const sessionId = this.tracker.storage.getSessionId();
    const userId = this.tracker.userId.getId();

    // Pass session ID — SR team exposes setSessionId()
    if (typeof window.b2metric.setSessionId === 'function') {
      window.b2metric.setSessionId(sessionId);
    } else {
      this.tracker.logger.warn('[B2M SessionReplay] window.b2metric.setSessionId not found — SR team needs to expose it');
    }

    // Pass user ID — identify() already exists in SR SDK
    if (userId && typeof window.b2metric.identify === 'function') {
      window.b2metric.identify(userId);
    }
  }

  // ── Lifecycle proxies ─────────────────────────────────────────────────────

  start() {
    if (!this._loaded) {
      this.tracker.logger.warn('[B2M SessionReplay] start() called before script loaded');
      return;
    }
    if (typeof window.b2metric?.startSessionReplay === 'function') {
      if (this.tracker.config.sessionReplayUseCustomSessionId) {
        // Custom-session-id mode: SR expects the session ID as an argument
        // because it intentionally did not generate one of its own.
        const sessionId = this.tracker.storage.getSessionId();
        window.b2metric.startSessionReplay(sessionId);
      } else {
        window.b2metric.startSessionReplay();
      }
      this.tracker.logger.log('[B2M SessionReplay] Recording started');
    }
  }

  stop() {
    if (!this._loaded) return;
    if (typeof window.b2metric?.stopSessionReplay === 'function') {
      window.b2metric.stopSessionReplay();
      this.tracker.logger.log('[B2M SessionReplay] Recording stopped');
    }
  }

  isActive() {
    if (!this._loaded || !window.b2metric?.isSessionReplayActive) return false;
    return window.b2metric.isSessionReplayActive();
  }

  /**
   * Call when session ID rotates (new session) so SR stays in sync.
   */
  onSessionChange() {
    this._sync();
  }

  /**
   * Call when external user ID changes so SR stays in sync.
   */
  onUserChange() {
    this._sync();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _injectScript(src, attrs = {}) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      // Do NOT set async — SR SDK uses document.currentScript which is
      // only reliable when the script executes synchronously after load.
      Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
      script.onload = resolve;
      script.onerror = () => reject(new Error(`Failed to load: ${src}`));
      this._scriptEl = script;
      document.head.appendChild(script);
    });
  }

  _waitForLoad() {
    return new Promise(resolve => {
      const check = setInterval(() => {
        if (!this._loading) {
          clearInterval(check);
          resolve(this._loaded);
        }
      }, 50);
    });
  }
}
